/**
 * CORE-BJ — an audit write that fails during a support session is LOUD.
 *
 * DoD 6: "an audit write failure during a support session is loud". R1 M13 made
 * the per-action writers (`useSupportAudit`) warn the support operator once per
 * session; the session BRACKETS (started / switched view / ended) written by
 * `SupportModeContext` still only `console.warn`ed (R4 audit G6), so a session
 * whose opening or closing was never recorded looked exactly like one that was.
 *
 * One mechanism for both: log every failure, and tell the operator once per
 * support session (a toast per failed page view would be noise; silence is the
 * defect). Lives here, not in the hook, so the context can use it without an
 * import cycle (the hook imports the context).
 */
import { toast } from '@/hooks/use-toast';

const warnedSessions = new Set<string>();

export function reportSupportAuditFailure(sessionId: string, what: string, error: unknown): void {
  console.error(`[support-audit] ${what} was NOT recorded:`, error);
  if (warnedSessions.has(sessionId)) return;
  warnedSessions.add(sessionId);
  toast({
    title: 'Support audit is not being recorded',
    description: `${what.charAt(0).toUpperCase()}${what.slice(1)} could not be written to the audit log. Stop and report this before continuing.`,
    variant: 'destructive',
  });
}

/**
 * CORE-BJ (Core-100 R4) — the server bounds person-scoped (USER mode) support access to
 * `support_user_mode_max_lifetime()` from the moment it begins (migration 20270206020900,
 * 1 hour, least-permissive default pending the owner). This mirrors that bound ONLY for
 * display, so the operator's countdown is never longer than the server allows; the server
 * remains the authority. Pinned to the migration by tests/unit/core100/supportBracketLoudness.test.tsx.
 */
export const SUPPORT_USER_MODE_MAX_MINUTES = 60;

export function userModeDisplayExpiry(currentExpiresAt: string, now: Date): string {
  const cap = now.getTime() + SUPPORT_USER_MODE_MAX_MINUTES * 60_000;
  const current = Date.parse(currentExpiresAt);
  return new Date(Number.isFinite(current) ? Math.min(current, cap) : cap).toISOString();
}

/** Test seam: forget which sessions were already warned. */
export function __resetSupportAuditFailureWarnings(): void {
  warnedSessions.clear();
}
